/**
 * Extracts the holder's name from text content of a Social Security PDF.
 *
 * Strategy: capture up to 40 characters immediately to the right of the word "Nome". * The & character is included in the allowed set so names like"A & B" are matched.
 */

const PT_UPPER_CHARS = 'A-ZÁÀÂÃÉÈÊÍÏÓÔÕÚÜÇÑ&\\-\\.';

// Capture up to 40 chars to the right of "Nome" (with optional whitespace separator)
const NAME_PATTERN_40 = new RegExp(
  `\\bNome\\s+([${PT_UPPER_CHARS}][${PT_UPPER_CHARS}\\s]{1,39})`,
  'i'
);

/**
 * Sanitizes a name string for use as a filename.
 * Strips accents/diacritics to plain ASCII, removes characters invalid in filenames.
 */
export function sanitizeFilename(name: string): string {
  return name
    .trim()
    .replace(/[&\-\.]/g, ' ')                // replace &, - and . with space
    .normalize('NFD')                        // decompose accented chars into base + diacritic
    .replace(/[\u0300-\u036f]/g, '')         // remove all diacritic marks
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '') // remove invalid filename chars
    .replace(/\s+/g, ' ')                    // collapse multiple spaces
    .trim();
}

/**
 * Extracts the holder name from the full text of a PDF.
 * Returns null if no name is found.
 */
export function extractName(fullText: string): string | null {
  // Strategy 1: "Nome CARLOS MANUEL..." — up to 40 chars to the right
  const match1 = NAME_PATTERN_40.exec(fullText);
  if (match1 && match1[1]) {
    // Trim trailing partial word that may bleed into next field
    const raw = match1[1].trim().replace(/\s+/g, ' ');
    // Stop at the first occurrence of a known field keyword that may follow
    const stopPattern = /\s+(?:Número|NIF|NISS|Data|Valor|\d)/i;
    const stopMatch = stopPattern.exec(raw);
    const candidate = stopMatch ? raw.slice(0, stopMatch.index).trim() : raw;
    if (candidate.length >= 3) return candidate;
  }

  // Strategy 2: "Nome" on its own line, name on the next non-empty line
  const lines = fullText.split('\n').map((l) => l.trim()).filter(Boolean);
  for (let i = 0; i < lines.length - 1; i++) {
    if (lines[i].toLowerCase() === 'nome') {
      const nextLine = lines[i + 1];
      // Allow & in the line check
      if (nextLine && /^[A-ZÁÀÂÃÉÈÊÍÏÓÔÕÚÜÇÑ&\-\.\s]{3,}$/i.test(nextLine)) {
        const candidate = nextLine.trim().replace(/\s+/g, ' ').slice(0, 40);
        if (candidate.length >= 3) return candidate;
      }
    }
  }

  return null;
}